import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface OperationalCountry {
  id: string;
  code: string;
  name: string;
  flag_emoji: string | null;
  currency_code: string | null;
  currency_symbol: string | null;
  phone_prefix: string | null;
  is_active: boolean;
  /** Per-country config overrides (issue #378 phase 4) — see countryConfig.ts. */
  settings?: Record<string, unknown> | null;
  /** Explicit admin-set primary country flag — see 20260721050000. */
  is_primary?: boolean;
}

export interface OperationalCity {
  id: string;
  country_id: string;
  name: string;
  region: string | null;
  is_active: boolean;
}

/**
 * Fetches active operational countries and cities from the admin-controlled
 * operational_countries / operational_cities tables.
 *
 * This is the PRIMARY source of truth for all location dropdowns across the app:
 * - Helper onboarding location step
 * - Customer onboarding / job posting location
 * - Admin panels
 *
 * Falls back to Uganda defaults if DB is unavailable.
 */
export function useOperationalRegions() {
  const { data: countries = [], isLoading: loadingCountries } = useQuery({
    queryKey: ['operational-countries'],
    queryFn: async (): Promise<OperationalCountry[]> => {
      let { data, error } = await db
        .from('operational_countries')
        .select('id, code, name, flag_emoji, currency_code, currency_symbol, phone_prefix, is_active, settings, is_primary')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) {
        // settings/is_primary columns may not exist yet on this environment
        // (migrations 20260719140000 / 20260721050000 pending) — retry
        // without them rather than falling back.
        ({ data, error } = await db
          .from('operational_countries')
          .select('id, code, name, flag_emoji, currency_code, currency_symbol, phone_prefix, is_active')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }));
      }
      if (error || !data || data.length === 0) {
        // Fallback to Uganda
        return [{ id: 'ug-fallback', code: 'UG', name: 'Uganda', flag_emoji: '🇺🇬', currency_code: 'UGX', currency_symbol: 'UGX', phone_prefix: '+256', is_active: true, is_primary: true }];
      }
      return data as OperationalCountry[];
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: true,
    retry: false,
  });

  const { data: cities = [], isLoading: loadingCities } = useQuery({
    queryKey: ['operational-cities'],
    queryFn: async (): Promise<OperationalCity[]> => {
      const { data, error } = await db
        .from('operational_cities')
        .select('id, country_id, name, region, is_active')
        .eq('is_active', true)
        .order('name', { ascending: true });   // order by name — always works, no sort_order dependency
      if (error || !data) return [];
      return data as OperationalCity[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - cities are stable admin data
    gcTime: 30 * 60 * 1000,
    refetchOnMount: true,
    retry: false,
  });

  /**
   * Get cities for a given country code, country id, or country name.
   * Supports lookup by any of: ISO code (e.g. "UG"), UUID, or full name (e.g. "Uganda").
   */
  function getCitiesForCountry(countryCodeOrIdOrName: string): OperationalCity[] {
    if (!countryCodeOrIdOrName) return [];
    const country = countries.find(
      c =>
        c.code === countryCodeOrIdOrName ||
        c.id === countryCodeOrIdOrName ||
        c.name === countryCodeOrIdOrName ||
        c.name.toLowerCase() === countryCodeOrIdOrName.toLowerCase()
    );
    if (!country) return [];
    return cities.filter(c => c.country_id === country.id);
  }

  /**
   * City names as a flat string array, optionally filtered by country code, id, or name.
   * Falls back to Uganda cities only if the entire cities DB is empty.
   */
  function getCityNames(countryCodeOrIdOrName?: string): string[] {
    if (!countryCodeOrIdOrName) {
      if (cities.length === 0) {
        return ['Kampala', 'Entebbe', 'Jinja', 'Mbarara', 'Gulu', 'Lira', 'Mbale', 'Masaka', 'Arua', 'Wakiso'];
      }
      // Dedupe by name — duplicate rows can exist across two
      // operational_countries entries that resolve to the same market
      // (e.g. a legacy duplicate country row), which would otherwise
      // surface as "Arua, Arua..." in location pickers.
      return Array.from(new Set(cities.map(c => c.name)));
    }
    const filtered = getCitiesForCountry(countryCodeOrIdOrName);
    if (filtered.length === 0) {
      return [];
    }
    return Array.from(new Set(filtered.map(c => c.name)));
  }

  return {
    countries,
    cities,
    getCitiesForCountry,
    getCityNames,
    countryNames: countries.map(c => c.name),
    countryCodes: countries.map(c => c.code),
    isLoading: loadingCountries || loadingCities,
    // Prefer the admin-set is_primary flag; fall back to sort_order for
    // resilience if the flag column isn't present yet or nothing is flagged.
    primaryCountry: countries.find(c => c.is_primary) ?? countries[0] ?? null,
  };
}
